<template>
  <Dialog :open="isOpen" @update:open="togglePopover">
    <DialogTrigger as-child>
      <Button
        type="button"
        variant="ghost"
        class="rounded-l-none px-3 hover:bg-black/5"
        :disabled="disabled"
        @click="togglePopover"
      >
        <Pencil class="h-4 w-4" />
      </Button>
    </DialogTrigger>
    <DialogContent class="max-h-screen overflow-scroll sm:max-w-[500px]">
      <DialogHeader>
        <DialogTitle class="mb-2 text-white">{{ t('input.changeEmail.title') }}</DialogTitle>
        <DialogDescription class="text-balance">{{
          t('input.changeEmail.description')
        }}</DialogDescription>
      </DialogHeader>
      <form class="form-wrapper" @submit.prevent="onSubmit">
        <InputWrapped
          v-model="newEmail"
          :placeholder="t('input.label.newEmail')"
          :label="t('input.label.newEmail')"
          name="newEmail"
          type="email"
          v-bind="newEmailAttrs"
          :error="errors.newEmail"
          :disabled="isSendChangeEmailFetching"
        />
        <InputWrapped
          v-model="newEmailAgain"
          :placeholder="t('input.label.newEmailAgain')"
          :label="t('input.label.newEmailAgain')"
          name="newEmailAgain"
          type="email"
          v-bind="newEmailAgainAttrs"
          :error="errors.newEmailAgain"
          :disabled="isSendChangeEmailFetching"
        />
        <Button
          type="submit"
          :disabled="isSendChangeEmailFetching"
          :loading="isSendChangeEmailFetching"
          class="w-full"
        >
          <Loader2
            v-if="isSendChangeEmailFetching"
            class="mr-2 h-4 w-4 animate-spin stroke-white"
          />
          {{ t('input.changeEmail.buttonLabel') }}
        </Button>
        <InfoBar
          v-if="sendChangeEmailError"
          variant="destructive"
          :title="t('common.error')"
          :message="sendChangeEmailError?.message || t('common.tryAgain')"
          @close="sendChangeEmailError = null"
        />
        <InfoBar
          v-if="sendChangeEmailData"
          variant="success"
          :title="t('common.success')"
          :message="sendChangeEmailData?.message || t('common.success')"
          @close="sendChangeEmailData = null"
        />
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@ui/dialog';
import Button from '@ui/button/Button.vue';
import InputWrapped from '@modules/InputWrapped/InputWrapped.vue';
import { Loader2, Pencil } from 'lucide-vue-next';
import InfoBar from '@generics/InfoBar.vue';
import { useAccountStore } from '@stores/account.store';
import * as yup from 'yup';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm } from 'vee-validate';
import { watch } from 'vue';
import { storeToRefs } from 'pinia';

const props = defineProps<{
  disabled?: boolean;
}>();

const isOpen = defineModel<boolean>('modelValue', {
  required: true,
});

const { t } = useI18n();
const accountStore = useAccountStore();
const { sendChangeEmailData, sendChangeEmailError, isSendChangeEmailFetching } =
  storeToRefs(accountStore);

const schema = toTypedSchema(
  yup.object({
    newEmail: yup.string().required(t('errors.required')).email(t('errors.invalidEmail')),
    newEmailAgain: yup
      .string()
      .required(t('errors.required'))
      .oneOf([yup.ref('newEmail')], t('errors.emailsMustMatch')),
  }),
);

interface ChangeEmailForm {
  newEmail: string;
  newEmailAgain: string;
}

const { errors, handleSubmit, defineField, values } = useForm<ChangeEmailForm>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: {
    newEmail: '',
    newEmailAgain: '',
  },
});
const [newEmail, newEmailAttrs] = defineField('newEmail', {
  validateOnModelUpdate: false,
  validateOnBlur: true,
});
const [newEmailAgain, newEmailAgainAttrs] = defineField('newEmailAgain', {
  validateOnModelUpdate: false,
  validateOnBlur: true,
});

const togglePopover = () => {
  if (props.disabled) return;
  if (isSendChangeEmailFetching.value && isOpen.value) return;
  isOpen.value = !isOpen.value;
};

const onSubmit = handleSubmit((values) => {
  if (props.disabled) return;
  accountStore.sendChangeEmail(values.newEmail);
});

watch(
  values,
  () => {
    sendChangeEmailError.value = null;
    sendChangeEmailData.value = null;
  },
  { deep: true },
);
</script>
